import { formatPlainDate, isAirAdmin, PrismaEnums } from '@airreading/common';
import { Schedule } from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Ellipsis } from 'lucide-react';
import { z } from 'zod';

import { InfoPage } from '@/components/info-page.js';
import { createTableRowSelectionCell, EntityTable } from '@/components/table/entity-table.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { TableFilterInputField } from '@/components/table/table-filter-field.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';
import { Button } from '@/components/ui/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { EmptyArray, getSchedules } from '@/lib/api-calls.js';
import { clearCurrentRowSelection, getCurrentRowSelectionCount, useRowsBulkSelection } from '@/lib/bulk-selection.js';
import { useErrorToast } from '@/lib/error-handling.js';
import { paginationRequestSchema } from '@/lib/pagination.js';
import { usePaginatedFilters } from '@/lib/filtering.js';
import { CreateEditSchedule } from '@/routes/_layout/scheduling/schedules/-components/create-edit-schedule.js';
import { UpdateScheduleStatus } from '@/routes/_layout/scheduling/schedules/-components/update-schedule-status.js';
import { useMemo } from 'react';
import { formatScheduleBreak, formatScheduleDates, formatStudentClassTimeAbbr } from './-components/formatting.js';
import { validateRouteRole } from '@/lib/routing.js';

const schedulesSearchSchema = paginationRequestSchema.extend({
  statuses: z.array(z.nativeEnum(PrismaEnums.ScheduleStatus)).optional(),
  query: z.string().optional(),
});

const allScheduleStatuesOptions = Object.values(PrismaEnums.ScheduleStatus).map((status) => ({
  label: status,
  value: status,
}));

export const Route = createFileRoute('/_layout/scheduling/schedules/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: SchedulesIndex,
  validateSearch: schedulesSearchSchema,
});

const columnHelper = createColumnHelper<Schedule>();
const columns = [
  createTableRowSelectionCell(columnHelper),

  columnHelper.accessor('name', {
    id: 'name',
    header: 'Schedule name',
  }),

  columnHelper.accessor((row) => row, {
    id: 'Dates',
    header: 'Dates',
    cell: ({ getValue }) => {
      return formatScheduleDates(getValue());
    },
  }),

  columnHelper.accessor('classTimes', {
    id: 'meetingTimes',
    header: 'Class times',
    cell: ({ getValue }) => {
      return (
        <div>
          {getValue().map((classTime) => (
            <div key={classTime.id}>{formatStudentClassTimeAbbr(classTime)}</div>
          ))}
        </div>
      );
    },
  }),

  columnHelper.accessor('scheduleBreaks', {
    id: 'Breaks',
    header: 'Breaks',
    cell: ({ getValue }) => {
      const breaks = getValue();
      return (
        <div>
          {breaks.map((scheduleBreak) => (
            <div key={scheduleBreak.name}>{formatScheduleBreak(scheduleBreak)}</div>
          ))}
        </div>
      );
    },
  }),

  columnHelper.accessor('school', {
    id: 'School',
    header: 'School',
    cell: ({ getValue }) => {
      return <div>{getValue().name}</div>;
    },
  }),

  columnHelper.accessor('practicePlainDate', {
    id: 'practiceDate',
    header: 'Practice class date',
    cell: ({ getValue }) => {
      const practiceDate = getValue();
      return practiceDate ? <div>{formatPlainDate(practiceDate)}</div> : <div>Not set</div>;
    },
  }),
  columnHelper.accessor('diagnosticPlainDate', {
    id: 'diagnosticDate',
    header: 'Diagnostic class date',
    cell: ({ getValue }) => {
      const diagnosticDate = getValue();
      return diagnosticDate ? <div>{formatPlainDate(diagnosticDate)}</div> : <div>Not set</div>;
    },
  }),
  columnHelper.accessor((row) => row, {
    id: 'actions',
    header: '',
    cell: ({ getValue }) => {
      return (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex h-8 w-8 p-0 data-[state=open]:bg-muted">
              <Ellipsis className="h-4 w-4" />
              <span className="sr-only">Open action menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <CreateEditSchedule
              from={{
                fromSchedule: getValue(),
                mode: 'edit',
              }}
              modalTitle="Edit schedule"
              trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Edit</DropdownMenuItem>}
            />
            <CreateEditSchedule
              from={{
                fromSchedule: getValue(),
                mode: 'create',
              }}
              trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Clone</DropdownMenuItem>}
            />
            <UpdateScheduleStatus
              scheduleOrIds={getValue()}
              trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Update status</DropdownMenuItem>}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  }),
];

function SchedulesIndex() {
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[scheduleNameQuery, activeScheduleNameQuery], setScheduleNameQuery],
      statuses: [[selectedStatuses, activeSelectedStatuses], setSelectedStatuses],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'statuses']);

  const {
    data: schedulesResponse,
    isPending,
    error,
    refetch,
  } = useQuery(
    getSchedules({
      pageIndex,
      pageSize,
      statuses: activeSelectedStatuses?.join(','),
      query: activeScheduleNameQuery,
    }),
  );

  useErrorToast(error, 'Error loading schedules. Please try again.');
  const schedules = useMemo(() => schedulesResponse?.schedules || EmptyArray, [schedulesResponse]);

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<Schedule>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => schedules.find((s) => s.id === id),
    });

  const table = useReactTable({
    data: schedules,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
      rowSelection,
    },
    onRowSelectionChange,
    getRowId,
    rowCount: schedulesResponse?.total || 0,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    defaultColumn: {
      enableSorting: false,
    },
  });

  return (
    <InfoPage headerText="Schedules">
      <EntityTable
        table={table}
        isLoading={isPending}
        showPagination
        emptyState={'No schedules found'}
        hasError={!!error}
        errorState={<TableErrorState refetchAction={refetch} />}
        bulkActionsMenuItems={
          <>
            {rowDataSelection.length > 0 && (
              <>
                <UpdateScheduleStatus
                  scheduleOrIds={Object.keys(rowSelection)}
                  trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Update status</DropdownMenuItem>}
                  onSuccess={resetAllRowSelection}
                />
              </>
            )}
          </>
        }
        filters={{
          filterLeftComponents: (
            <>
              <TableFilterInputField
                placeholder="Search schedules by name"
                value={scheduleNameQuery ?? ''}
                onChange={(event) => {
                  setScheduleNameQuery(event.target.value);
                }}
              />
              <TableFilterDropdown
                title="Status"
                options={allScheduleStatuesOptions}
                onChange={setSelectedStatuses}
                selectedValues={selectedStatuses ?? []}
              />
              <TableFilterResetButton show={true} onClick={resetFilters} />
              <TableFilterResetButton
                show={getCurrentRowSelectionCount(table) > 0}
                onClick={() => clearCurrentRowSelection(table)}
                label={`Clear ${getCurrentRowSelectionCount(table)} selected row(s)`}
              />
            </>
          ),
        }}
        topBarRightComponents={
          <>
            <CreateEditSchedule />
          </>
        }
      />
    </InfoPage>
  );
}
